import * as React from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { Loader2 } from 'lucide-react';
import { t } from '../../i18n';

/**
 * UI primitives.
 *
 * shadcn-style (copied in, not a dependency) so the visual language can be
 * tuned to `design/common.jsx` without fighting a library's defaults. Every
 * component here is used across at least three screens; anything narrower
 * lives with its feature.
 */

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

// ── Button ──────────────────────────────────────────────────────────────────

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sk-md text-sm font-semibold ' +
    'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo/60 ' +
    'focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary: 'bg-indigo text-white shadow-sk-xs hover:bg-indigo-700 active:bg-indigo-800',
        secondary: 'bg-surface text-ink-1 border border-outline-field hover:bg-surface-variant',
        ghost: 'text-ink-2 hover:bg-surface-variant hover:text-ink-1',
        danger: 'bg-danger text-white shadow-sk-xs hover:bg-danger-ink',
        // For the one primary action on a mostly-neutral screen.
        accent: 'bg-teal text-white shadow-sk-xs hover:bg-teal-700',
        link: 'text-indigo underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-8 px-3 text-xs [&_svg]:size-3.5',
        md: 'h-10 px-4 [&_svg]:size-4',
        lg: 'h-11 px-6 text-[15px] [&_svg]:size-4',
        icon: 'size-9 [&_svg]:size-4',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Shows a spinner and blocks further clicks. */
  loading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        disabled={disabled ?? loading}
        {...props}
      >
        {/*
          `Slottable` marks which child the Slot should merge into. Without it,
          `asChild` hands Slot two children — the spinner slot and `children` —
          and Radix throws "Slot failed to slot onto its children". Wrapped this
          way, `<Button asChild><Link/></Button>` still renders a spinner while
          loading instead of crashing the page.
        */}
        {loading ? <Loader2 className="animate-spin" aria-hidden /> : null}
        <Slottable>{children}</Slottable>
      </Comp>
    );
  },
);
Button.displayName = 'Button';

// ── Card ────────────────────────────────────────────────────────────────────

export const Card = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('sk-card', className)} {...props} />
  ),
);
Card.displayName = 'Card';

export const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col gap-1 px-5 pb-3 pt-5', className)} {...props} />
  ),
);
CardHeader.displayName = 'CardHeader';

export const CardTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, children, ...props }, ref) => (
    <h3 ref={ref} className={cn('text-[15px] font-bold tracking-display', className)} {...props}>
      {children}
    </h3>
  ),
);
CardTitle.displayName = 'CardTitle';

export const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('text-[13px] leading-relaxed text-ink-2', className)} {...props} />
));
CardDescription.displayName = 'CardDescription';

export const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn('px-5 pb-5', className)} {...props} />,
);
CardContent.displayName = 'CardContent';

export const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex items-center gap-2 border-t border-outline-soft px-5 py-3', className)}
      {...props}
    />
  ),
);
CardFooter.displayName = 'CardFooter';

// ── Badge ───────────────────────────────────────────────────────────────────

const badgeVariants = cva(
  'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-2xs font-bold leading-4 whitespace-nowrap',
  {
    variants: {
      tone: {
        neutral: 'bg-surface-variant text-ink-2',
        indigo: 'bg-indigo-soft text-indigo-ink',
        teal: 'bg-teal-soft text-teal-ink',
        marigold: 'bg-marigold-soft text-marigold-ink',
        success: 'bg-success-soft text-success-ink',
        warning: 'bg-warning-soft text-warning-ink',
        danger: 'bg-danger-soft text-danger-ink',
        info: 'bg-info-soft text-info-ink',
        outline: 'border border-outline text-ink-2',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export function Badge({ className, tone, ...props }: BadgeProps): React.JSX.Element {
  return <span className={cn(badgeVariants({ tone }), className)} {...props} />;
}

// ── Input ───────────────────────────────────────────────────────────────────

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, invalid, ...props }, ref) => (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        'h-10 w-full rounded-sk-md border bg-surface px-3 text-sm text-ink-1 transition-colors',
        'placeholder:text-ink-3 focus-visible:outline-none focus-visible:ring-2',
        'disabled:cursor-not-allowed disabled:bg-surface-variant disabled:text-ink-3',
        invalid
          ? 'border-danger focus-visible:ring-danger/40'
          : 'border-outline-field focus-visible:border-indigo focus-visible:ring-indigo/25',
        className,
      )}
      {...props}
    />
  ),
);
Input.displayName = 'Input';

export const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }
>(({ className, invalid, ...props }, ref) => (
  <textarea
    ref={ref}
    aria-invalid={invalid || undefined}
    className={cn(
      'w-full rounded-sk-md border bg-surface px-3 py-2 text-sm text-ink-1 transition-colors',
      'placeholder:text-ink-3 focus-visible:outline-none focus-visible:ring-2',
      invalid
        ? 'border-danger focus-visible:ring-danger/40'
        : 'border-outline-field focus-visible:border-indigo focus-visible:ring-indigo/25',
      className,
    )}
    {...props}
  />
));
Textarea.displayName = 'Textarea';

// ── Field ───────────────────────────────────────────────────────────────────

export interface FieldProps {
  label: string;
  htmlFor?: string;
  error?: string | undefined;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
  className?: string;
}

/**
 * Label + control + error, in one place.
 *
 * Accessibility, all of it earned rather than assumed:
 *  - The error is `role="alert"`, so a screen reader announces a validation
 *    failure the moment it appears instead of leaving a blind user to find it.
 *  - The red asterisk is `aria-hidden` and paired with a visually-hidden
 *    "(required)". A bare `*` is announced as "star", which means nothing, and
 *    colour alone cannot carry the requirement (WCAG 1.4.1).
 *  - `describedBy` hands the caller the id to put on `aria-describedby`, so the
 *    hint and the error are actually associated with the control rather than
 *    just sitting near it.
 */
export function Field({
  label,
  htmlFor,
  error,
  hint,
  required,
  children,
  className,
}: FieldProps): React.JSX.Element {
  const generatedId = React.useId();
  const base = htmlFor ?? generatedId;
  const messageId = `${base}-message`;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={htmlFor} className="text-[13px] font-semibold text-ink-1">
        {label}
        {required ? (
          <>
            <span className="ml-0.5 text-danger" aria-hidden>
              *
            </span>
            <span className="sr-only">{' '}{t('(required)')}</span>
          </>
        ) : null}
      </label>
      {children}
      {error ? (
        <p id={messageId} role="alert" className="text-xs font-medium text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={messageId} className="text-xs text-ink-3">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The id `Field` gives its hint/error node.
 *
 * Pass the same `htmlFor` to both and wire it into the control's
 * `aria-describedby`; the form components below do this for you.
 */
export function fieldMessageId(fieldId: string): string {
  return `${fieldId}-message`;
}

// ── Avatar ──────────────────────────────────────────────────────────────────

/** Palette from `design/common.jsx`, so a person's colour matches on mobile. */
const AVATAR_PALETTE: [string, string][] = [
  ['#FEE2E2', '#B91C1C'],
  ['#FEF3C7', '#92400E'],
  ['#D1FAE5', '#047857'],
  ['#CCFBF1', '#0F766E'],
  ['#DBEAFE', '#1D4ED8'],
  ['#E0E7FF', '#3730A3'],
  ['#FCE7F3', '#9D174D'],
  ['#FFEDD5', '#9A3412'],
  ['#ECFCCB', '#3F6212'],
];

function avatarColours(name: string): [string, string] {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length] ?? ['#E5E7EB', '#475569'];
}

export interface AvatarProps {
  name: string;
  src?: string | null;
  size?: number;
  className?: string;
  /** Small coloured dot — presence, or a helper's attendance state. */
  status?: 'online' | 'offline' | 'warning' | null;
}

export function Avatar({ name, src, size = 36, className, status }: AvatarProps): React.JSX.Element {
  const [bg, fg] = avatarColours(name || '?');
  const initials = (name || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] ?? '')
    .join('')
    .toUpperCase();

  return (
    <span className={cn('relative inline-flex shrink-0', className)} style={{ width: size, height: size }}>
      {src ? (
        <img
          src={src}
          alt={name}
          width={size}
          height={size}
          className="rounded-full object-cover"
          style={{ width: size, height: size }}
        />
      ) : (
        <span
          aria-hidden
          className="flex items-center justify-center rounded-full font-display font-bold"
          style={{ width: size, height: size, background: bg, color: fg, fontSize: size * 0.4 }}
        >
          {initials}
        </span>
      )}
      {status ? (
        <span
          className={cn(
            'absolute -bottom-0.5 -right-0.5 rounded-full ring-2 ring-surface',
            status === 'online' && 'bg-success',
            status === 'offline' && 'bg-ink-3',
            status === 'warning' && 'bg-warning',
          )}
          style={{ width: size * 0.3, height: size * 0.3 }}
        />
      ) : null}
      <span className="sr-only">{name}</span>
    </span>
  );
}

// ── Skeleton / empty / error states ─────────────────────────────────────────

export function Skeleton({ className }: { className?: string }): React.JSX.Element {
  return <div className={cn('sk-skeleton', className)} aria-hidden />;
}

export interface EmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode;
  className?: string;
}

/**
 * The empty state.
 *
 * Always says what to do next, not just that there is nothing here — an empty
 * screen with no next step is where new societies stall.
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: EmptyStateProps): React.JSX.Element {
  return (
    <div className={cn('flex flex-col items-center justify-center px-6 py-14 text-center', className)}>
      {icon ? (
        <div className="mb-3 flex size-12 items-center justify-center rounded-sk-lg bg-indigo-soft text-indigo">
          {icon}
        </div>
      ) : null}
      <p className="font-display text-[15px] font-bold text-ink-1">{title}</p>
      {description ? <p className="mt-1 max-w-sm text-[13px] text-ink-2">{description}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

// ── Section header ──────────────────────────────────────────────────────────

export interface PageHeaderProps {
  title: string;
  description?: string;
  breadcrumb?: { label: string; href?: string }[];
  actions?: React.ReactNode;
}

export function PageHeader({
  title,
  description,
  breadcrumb,
  actions,
}: PageHeaderProps): React.JSX.Element {
  return (
    <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        {breadcrumb?.length ? (
          <nav aria-label={t('Breadcrumb')} className="mb-1 flex items-center gap-1.5 text-xs text-ink-3">
            {breadcrumb.map((crumb, index) => (
              <React.Fragment key={crumb.label}>
                {index > 0 ? <span aria-hidden>/</span> : null}
                {crumb.href ? (
                  <a href={crumb.href} className="hover:text-ink-2">
                    {crumb.label}
                  </a>
                ) : (
                  <span>{crumb.label}</span>
                )}
              </React.Fragment>
            ))}
          </nav>
        ) : null}
        <h1 className="truncate text-xl font-bold tracking-display">{title}</h1>
        {description ? <p className="mt-0.5 text-[13px] text-ink-2">{description}</p> : null}
      </div>
      {actions ? <div className="flex max-w-full flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  );
}

// ── Stat tile ───────────────────────────────────────────────────────────────

export interface StatProps {
  label: string;
  value: React.ReactNode;
  hint?: string;
  icon?: React.ReactNode;
  tone?: 'indigo' | 'teal' | 'marigold' | 'success' | 'danger' | 'neutral';
  /** Percentage change against the previous period. */
  delta?: number | null;
  loading?: boolean;
}

const TONE_CLASSES: Record<NonNullable<StatProps['tone']>, string> = {
  indigo: 'bg-indigo-soft text-indigo',
  teal: 'bg-teal-soft text-teal',
  marigold: 'bg-marigold-soft text-marigold-ink',
  success: 'bg-success-soft text-success-ink',
  danger: 'bg-danger-soft text-danger-ink',
  neutral: 'bg-surface-variant text-ink-2',
};

export function Stat({
  label,
  value,
  hint,
  icon,
  tone = 'neutral',
  delta,
  loading,
}: StatProps): React.JSX.Element {
  return (
    <div className="sk-card p-4">
      <div className="flex items-start justify-between gap-2">
        <p className="sk-label">{label}</p>
        {icon ? (
          <span className={cn('flex size-7 items-center justify-center rounded-sk-sm', TONE_CLASSES[tone])}>
            {icon}
          </span>
        ) : null}
      </div>
      {loading ? (
        <Skeleton className="mt-2 h-7 w-24" />
      ) : (
        <p className="mt-1.5 font-num text-2xl font-bold tracking-num text-ink-1">{value}</p>
      )}
      <div className="mt-1 flex items-center gap-1.5">
        {typeof delta === 'number' ? (
          <span
            className={cn(
              'text-xs font-bold',
              delta > 0 ? 'text-success-ink' : delta < 0 ? 'text-danger-ink' : 'text-ink-3',
            )}
          >
            {delta > 0 ? '+' : ''}
            {delta.toFixed(1)}%
          </span>
        ) : null}
        {hint ? <span className="text-xs text-ink-3">{hint}</span> : null}
      </div>
    </div>
  );
}
